import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Box,
  FormControlLabel,
  Switch,
} from '@mui/material';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { Customer } from '../../types';
import { customerService } from '../../services/customerService';
import { CustomerAddressEditor, EditableAddress } from '../../components/customers/CustomerAddressEditor';
import { validateAndNormalizeBdPhone } from '../../utils/phone';

const addressSchema = yup.object({
  address_type: yup.string().required('Address type is required'),
  street_address: yup.string().required('Street address is required'),
  city: yup.string().required('City is required'),
  map_link: yup.string().optional(),
  is_default: yup.boolean().optional(),
});

const schema = yup.object({
  first_name: yup.string().required('First name is required'),
  last_name: yup.string().transform((value) => value || undefined).optional(),
  email: yup.string().email('Invalid email').transform((value) => value || undefined).optional(),
  phone: yup
    .string()
    .transform((value) => value || undefined)
    .optional()
    .test(
      'bd-phone',
      'Phone number must start with 01 or +8801',
      (value) => !value || validateAndNormalizeBdPhone(value).valid,
    ),
  date_of_birth: yup.string().transform((value) => value || undefined).optional(),
  marketing_consent: yup.boolean().optional(),
  newsletter_subscription: yup.boolean().optional(),
  addresses: yup.array().of(addressSchema).optional(),
});

interface CustomerCreationData {
  first_name: string;
  last_name?: string;
  email?: string;
  phone?: string;
  date_of_birth?: string;
  marketing_consent?: boolean;
  newsletter_subscription?: boolean;
  addresses?: EditableAddress[];
}

interface CustomerCreationDialogProps {
  open: boolean;
  onClose: () => void;
  onCustomerCreated: (customer: Customer) => void;
}

export const CustomerCreationDialog: React.FC<CustomerCreationDialogProps> = ({
  open,
  onClose,
  onCustomerCreated,
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>('');
  const [addresses, setAddresses] = useState<EditableAddress[]>([]);

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<CustomerCreationData>({
    resolver: yupResolver(schema) as any,
    defaultValues: {
      first_name: '',
      last_name: '',
      email: '',
      phone: '',
      date_of_birth: '',
      marketing_consent: false,
      newsletter_subscription: false,
      addresses: [],
    },
  });

  useEffect(() => {
    if (open) {
      reset({
        first_name: '',
        last_name: '',
        email: '',
        phone: '',
        date_of_birth: '',
        marketing_consent: false,
        newsletter_subscription: false,
        addresses: [],
      });
      setAddresses([]);
      setError('');
    }
  }, [open, reset]);

  const handleFormSubmit = async (data: CustomerCreationData) => {
    try {
      setLoading(true);
      setError('');

      const customerData = {
        ...data,
        last_name: data.last_name || undefined,
        email: data.email || undefined,
        phone: data.phone
          ? validateAndNormalizeBdPhone(data.phone).normalized
          : undefined,
        date_of_birth: data.date_of_birth || undefined,
        is_active: true,
        marketing_consent: data.marketing_consent ?? false,
        newsletter_subscription: data.newsletter_subscription ?? false,
        addresses: addresses.length > 0 ? addresses : undefined,
      };

      const result = await customerService.createCustomer(customerData);
      onCustomerCreated(result);
      onClose();
    } catch (err: any) {
      console.error('Customer creation error:', err);

      let errorMessage = 'Failed to create customer';

      if (err.response?.data) {
        const errorData = err.response.data;

        if (Array.isArray(errorData)) {
          errorMessage = errorData.map((error: any) =>
            `${error.loc?.join(' -> ') || 'Field'}: ${error.msg}`
          ).join(', ');
        } else if (errorData.detail) {
          errorMessage = typeof errorData.detail === 'string'
            ? errorData.detail
            : JSON.stringify(errorData.detail);
        } else {
          errorMessage = JSON.stringify(errorData);
        }
      } else if (err.message) {
        errorMessage = err.message;
      }

      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    reset();
    setError('');
    setAddresses([]);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Add New Customer</DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Box component="form" sx={{ mt: 1 }}>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Controller
              name="first_name"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  margin="normal"
                  required
                  fullWidth
                  label="First Name"
                  error={!!errors.first_name}
                  helperText={errors.first_name?.message}
                  autoFocus
                />
              )}
            />

            <Controller
              name="last_name"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  margin="normal"
                  fullWidth
                  label="Last Name (Optional)"
                  error={!!errors.last_name}
                  helperText={errors.last_name?.message}
                />
              )}
            />
          </Box>

          <Controller
            name="email"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                margin="normal"
                fullWidth
                label="Email (Optional)"
                type="email"
                error={!!errors.email}
                helperText={errors.email?.message}
              />
            )}
          />

          <Controller
            name="phone"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                margin="normal"
                fullWidth
                label="Phone (Optional)"
                error={!!errors.phone}
                helperText={errors.phone?.message}
              />
            )}
          />

          <Controller
            name="date_of_birth"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                margin="normal"
                fullWidth
                label="Date of Birth (Optional)"
                type="date"
                InputLabelProps={{ shrink: true }}
                error={!!errors.date_of_birth}
                helperText={errors.date_of_birth?.message}
              />
            )}
          />

          <Box sx={{ mt: 2, display: 'flex', gap: 2 }}>
            <Controller
              name="marketing_consent"
              control={control}
              render={({ field }) => (
                <FormControlLabel
                  control={<Switch {...field} checked={field.value} />}
                  label="Marketing Consent"
                />
              )}
            />

            <Controller
              name="newsletter_subscription"
              control={control}
              render={({ field }) => (
                <FormControlLabel
                  control={<Switch {...field} checked={field.value} />}
                  label="Newsletter Subscription"
                />
              )}
            />
          </Box>

          {/* Addresses Section */}
          <CustomerAddressEditor addresses={addresses} onChange={setAddresses} />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit(handleFormSubmit)}
          variant="contained"
          disabled={loading}
        >
          {loading ? <CircularProgress size={24} /> : 'Create Customer'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
